import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { EnfafCheckboxComponent } from '../../../../../components/enfaf-checkbox/enfaf-checkbox.component';
import { EnfafIconComponent } from '../../../../../components/enfaf-icon/enfaf-icon.component';
import { EnfafInputComponent } from '../../../../../components/enfaf-input/enfaf-input.component';
import { EnfafInputSuffixComponent } from '../../../../../components/enfaf-input-suffix/enfaf-input-suffix.component';
import { PrimaryCtaButtonComponent } from '../../../../../components/primary-cta-button/primary-cta-button.component';
import { ProductVariant } from '../../../../../models/interfaces/product-variant.interface';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
    selector: 'app-product-variants',
    imports: [
    ReactiveFormsModule,
    MatIconModule,
    EnfafCheckboxComponent,
    EnfafIconComponent,
    EnfafInputComponent,
    EnfafInputSuffixComponent,
    PrimaryCtaButtonComponent
],
    templateUrl: './product-variants.component.html',
    styleUrl: './product-variants.component.scss'
})
export class ProductVariantsComponent implements OnInit {
  @Input() parentForm!: FormGroup;
  @Input() variants: ProductVariant[] = [];
  @Output() variantsChange = new EventEmitter<ProductVariant[]>();

  constructor(
    private sanitizer: DomSanitizer,
    private iconRegistry: MatIconRegistry
  ) {
    this.iconRegistry.addSvgIcon('trash', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/trash-red.svg'));
  }

  ngOnInit() {
    this.initializeVariantsFormArray();
    this.setupVariantsListener();
  }

  private initializeVariantsFormArray(): void {
    if (!this.parentForm.get('variants')) {
      this.parentForm.addControl('variants', new FormArray([]));
    }

    if (this.variants && this.variants.length > 0) {
      const variantsArray = this.variantsFormArray;
      variantsArray.clear();

      this.variants.forEach(variant => {
        variantsArray.push(this.createVariantFormGroup(variant));
      });
    }
  }

  private setupVariantsListener(): void {
    this.parentForm.get('hasVariants')?.valueChanges.subscribe((value) => {
      if (value && this.variantsFormArray.length === 0) {
        this.addEmptyVariant();
      } else if (!value) {
        this.clearAllVariants();
      }
    });

    this.variantsFormArray.valueChanges.subscribe((variants) => {
      this.variantsChange.emit(variants);
    });
  }

  get variantsFormArray(): FormArray {
    return this.parentForm.get('variants') as FormArray;
  }

  get hasVariantsControl(): FormControl {
    return this.parentForm.get('hasVariants') as FormControl;
  }

  createVariantFormGroup(variant?: ProductVariant): FormGroup {
    return new FormGroup({
      productVariantId: new FormControl(variant?.productVariantId),
      name: new FormControl(variant?.name, [Validators.required]),
      price: new FormControl(variant?.price, [Validators.required, Validators.min(0)])
    });
  }

  addEmptyVariant(): void {
    this.variantsFormArray.push(this.createVariantFormGroup());
  }

  removeVariant(index: number): void {
    this.variantsFormArray.removeAt(index);
  }

  private clearAllVariants(): void {
    while (this.variantsFormArray.length > 0) {
      this.variantsFormArray.removeAt(0);
    }
  }

  getVariantNameError(index: number): string | null {
    const variantControl = this.variantsFormArray.at(index);
    const nameControl = variantControl?.get('name');
    if (nameControl?.errors && nameControl.touched) {
      if (nameControl.errors['required']) return 'El nombre de la variante es requerido';
      if (nameControl.errors['minlength']) return 'El nombre debe tener al menos 1 carácter';
    }
    return null;
  }

  getVariantPriceError(index: number): string | null {
    const variantControl = this.variantsFormArray.at(index);
    const priceControl = variantControl?.get('price');
    if (priceControl?.errors && priceControl.touched) {
      if (priceControl.errors['required']) return 'El precio de la variante es requerido';
      if (priceControl.errors['min']) return 'El precio mínimo es 0';
    }
    return null;
  }
}
